import { Dialog, Drawer } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSpring } from 'react-spring';
import sleepIcon from '../../../../assets/icon/sleep.svg';
import AgeVerificationDialog from '../../../../components/AgeVerificationDialog/AgeVerificationDialog';
import AgeVerificationRejectedDialog from '../../../../components/AgeVerificationDialog/AgeVerificationRejectedDialog';
import ListFoodSuggested from '../../../../components/ListFoodSuggestedNew';
import { useNewOrderContext } from '../../../../context/NewOrderContext';
import { useSystemContext } from '../../../../context/SystemContext/SystemContext';
import { useUserMenuContext } from '../../../../context/UserMenuContext';
import useAgeVerificationGate from '../../../../hook/useAgeVerificationGate';
import { FoodDetailItem, FoodItem, RestaurantWithFoodType, SearchResultType } from '../../../../interfaces/types';
import { getPreOrderQuantityStatusForCartItem, isPreOrderDisabled } from '../../../../utils/preOrderDisabled';
import CustomizeDish from '../../../Menu/components/CustomizeDish';
import CategoryTabs from './components/CategoryTabs';
import FloatingCart from './components/FloatingCart';
import FoodList from './components/FoodList';
import RecommendedFoodDialog from './components/RecommendedFoodDialog';
import SearchBar from './components/SearchBar';
import { fetchGameDayData, GameDayData, generateTimeSlots } from '../../../../utils/timeSlots';

interface HomeTabNewProps {}

const HomeTabNew: React.FC<HomeTabNewProps> = () => {
  const { listRestaurantWithFood, homeMenuCategories, loading, loadingFoods, loadingFoodTrucks, userMenuByCategoryId, restaurantFoodMap, listRestaurantData } =
    useUserMenuContext();
  const { stadiumInfo, seatInfo, isPreOrder } = useSystemContext();

  const { updateOrderDetails, orderDetails } = useNewOrderContext();
  const { t } = useTranslation();
  const [searchResult, setSearchResult] = useState<SearchResultType>();
  const [input, setInput] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState<string>('');
  const [customDishData, setCustomDishData] = useState<any>(null);
  const [openCustomDish, setOpenCustomDish] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [activeFood, setActiveFood] = useState<{ [key: string]: number }>({});
  const [openDialog, setOpenDialog] = useState<{ value: boolean; type: string; maxQty?: number }>({ value: false, type: '' });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; foodId: string; categoryId: string; foodName: string }>({
    open: false,
    foodId: '',
    categoryId: '',
    foodName: '',
  });
  const [showRecommendedDialog, setShowRecommendedDialog] = useState(false);
  const [recommendedFoods, setRecommendedFoods] = useState<FoodDetailItem[]>([]);
  const [openedFromRecommended, setOpenedFromRecommended] = useState(false);
  const [showNoticeBanner, setShowNoticeBanner] = useState(() => localStorage.getItem('order_notice_dismissed') !== 'true');
  const [gameDayData, setGameDayData] = useState<GameDayData | null>(null);
  const {
    openAgeVerification,
    openAgeRejected,
    runWithAgeVerification,
    handleConfirmAgeVerification,
    handleCancelAgeVerification,
    handleCloseAgeVerification,
    handleCloseAgeRejected,
  } = useAgeVerificationGate();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  useEffect(() => {
    const loadGameDayData = async () => {
      if (!stadiumInfo?.id || !seatInfo?.order_date) {
        setGameDayData(null);
        return;
      }

      const data = await fetchGameDayData(stadiumInfo.id, seatInfo.order_date);
      setGameDayData(data);
    };

    loadGameDayData();
  }, [seatInfo?.order_date, stadiumInfo?.id]);

  // Helper function to get restaurant name by restaurantId
  const getRestaurantName = (restaurantId: string) => {
    const restaurant = listRestaurantWithFood.find((r) => r.id === restaurantId);
    return restaurant?.name || '';
  };

  const enable_drink_quantity_control = stadiumInfo?.enable_drink_quantity_control;
  const enable_food_quantity_control = stadiumInfo?.enable_food_quantity_control;
  const max_drink_quantity = stadiumInfo?.max_drink_quantity || 0;
  const max_food_quantity = stadiumInfo?.max_food_quantity || 0;

  const FoodTotalCount = useMemo(() => {
    return orderDetails.foods.reduce((total, food) => total + food.item_quantity, 0);
  }, [orderDetails.foods]);

  const menuFoodIds = useMemo(() => {
    const ids = new Set<string>();
    homeMenuCategories.forEach((cat) => {
      cat.foods.forEach((f) => {
        if (f.id) ids.add(f.id);
        if ((f as FoodDetailItem).food_id) ids.add((f as FoodDetailItem).food_id!);
      });
    });
    return ids;
  }, [homeMenuCategories]);

  const TotalOrderPrice = useMemo(() => {
    return Number(orderDetails.foods.reduce((total, food) => total + food.item_quantity * food.item_price, 0).toFixed(2));
  }, [orderDetails.foods]);

  const cartAnimation = useSpring({
    transform: showCart ? 'translate(-50%, 0%)' : 'translate(-50%, 100%)',
    opacity: showCart ? 1 : 0,
  });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setInput(inputValue);

    if (!inputValue.trim()) {
      setSearchResult(undefined);
      return;
    }

    const searchLower = inputValue.toLowerCase();

    const filteredFoods: RestaurantWithFoodType[] = [];
    const restaurantMap = new Map<string, RestaurantWithFoodType>();

    homeMenuCategories.forEach((category) => {
      category.foods.forEach((food) => {
        if (food.name.toLowerCase().includes(searchLower)) {
          if (!restaurantMap.has(food.restaurantId)) {
            const restaurant = listRestaurantWithFood.find((r) => r.id === food.restaurantId);
            if (restaurant) {
              restaurantMap.set(food.restaurantId, {
                ...restaurant,
                food_details: [],
              });
            }
          }

          const restaurantEntry = restaurantMap.get(food.restaurantId);
          if (restaurantEntry) {
            if (!restaurantEntry.food_details.find((f: FoodDetailItem) => f.id === food.id)) {
              restaurantEntry.food_details.push(food);
            }
          }
        }
      });
    });

    filteredFoods.push(...Array.from(restaurantMap.values()));

    setSearchResult({
      restaurants: [],
      foods: filteredFoods,
    });
  };

  const allFoodsFromCategories = useMemo(() => {
    return homeMenuCategories.flatMap((cat) => cat.foods);
  }, [homeMenuCategories]);

  const getMasterId = (itemId?: string, foodId?: string) => {
    if (foodId) return foodId;
    const foodItem = allFoodsFromCategories.find((f) => f.id === itemId || f.food_id === itemId);
    return foodItem?.food_id || itemId || '';
  };

  const isSameCartFood = (cartFood: FoodItem, food: Pick<FoodDetailItem, 'id' | 'food_id'>) => getMasterId(cartFood.item_food_id) === getMasterId(food.id, food.food_id);

  const findExistingCartFood = (food: Pick<FoodDetailItem, 'id' | 'food_id'>) => {
    return orderDetails.foods.find((cartFood) => isSameCartFood(cartFood, food));
  };

  const getFoodDetailForCartItem = (cartItem: FoodItem) => {
    const masterId = getMasterId(cartItem.item_food_id);

    for (const category of homeMenuCategories) {
      if (cartItem.item_category_id && category.id !== cartItem.item_category_id) continue;

      const foodDetail = category.foods.find((food) => {
        const foodMasterId = food.food_id || food.id;
        return food.id === cartItem.item_food_id || foodMasterId === masterId;
      });

      if (foodDetail) return foodDetail;
    }

    const allRestaurantFoods = Object.values(restaurantFoodMap ?? {}).flat() as FoodDetailItem[];
    return allRestaurantFoods.find((food) => {
      const foodMasterId = food.food_id || food.id;
      return food.id === cartItem.item_food_id || foodMasterId === masterId;
    });
  };

  const filterAvailablePreOrderCartItems = (cartItems: FoodItem[]) => {
    return cartItems.filter((cartItem) => {
      const foodDetail = getFoodDetailForCartItem(cartItem);
      if (!foodDetail) return false;

      const restaurantId = cartItem.restaurantId || foodDetail.restaurantId;
      const restaurant = listRestaurantData?.find((item) => item.id === restaurantId);
      const preOrderStatus = isPreOrderDisabled(foodDetail, restaurant, seatInfo?.order_date, isPreOrder, orderDetails.delivery_request, gameDayData);

      return !preOrderStatus.disabled;
    });
  };

  const filterAvailableSameDayCartItems = (cartItems: FoodItem[]) => {
    return cartItems.filter((cartItem) => {
      const foodDetail = getFoodDetailForCartItem(cartItem);
      return !!foodDetail && foodDetail.enabled !== false;
    });
  };

  const getCurrentAvailableCartItems = () => {
    const availableFoods = isPreOrder ? filterAvailablePreOrderCartItems(orderDetails.foods) : filterAvailableSameDayCartItems(orderDetails.foods);
    const availablePromotionFoods = isPreOrder
      ? filterAvailablePreOrderCartItems(orderDetails.promotion_foods ?? [])
      : filterAvailableSameDayCartItems(orderDetails.promotion_foods ?? []);

    return { availableFoods, availablePromotionFoods };
  };

  const toggleCart = (item: FoodDetailItem, categoryId: string) => {
    const newItem = { ...item, categories: [categoryId], categoryId };
    setCustomDishData(newItem);
    setOpenCustomDish(true);
  };

  // Helper function to get recommended foods for a dish (only foods that exist in current menu)
  const getRecommendedFoods = (foodId: string, categoryId: string): FoodDetailItem[] => {
    if (!categoryId || !userMenuByCategoryId?.[categoryId]) return [];

    const categoryData = userMenuByCategoryId[categoryId];
    const foodRef = categoryData?.foodsById?.[foodId];

    if (!foodRef?.recommended_food || foodRef.recommended_food.length === 0) return [];

    const recommendedFoodItems: FoodDetailItem[] = [];
    foodRef.recommended_food.forEach((rf: any) => {
      if (rf.enabled && rf.id) {
        const allFoods = Object.values(restaurantFoodMap ?? {}).flat() as FoodDetailItem[];
        const foodItem = allFoods.find((f: FoodDetailItem) => f.id === rf.id || f.food_id === rf.id);
        if (!foodItem) return;
        if (isPreOrder) {
          if (foodItem.enabled_pre_order !== true) return;
        } else if (!foodItem.enabled) {
          return;
        }

        const rfMetaInCategory = categoryData?.foodsById?.[rf.id];
        if (rfMetaInCategory && !rfMetaInCategory.enabled) return;

        const idInMenu = menuFoodIds.has(foodItem.id) || (foodItem.food_id && menuFoodIds.has(foodItem.food_id));
        if (!idInMenu) return;

        recommendedFoodItems.push(foodItem);
      }
    });

    return recommendedFoodItems;
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    if (!newOpen) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
    setOpenCustomDish(newOpen);
  };

  const editItemOrder = (item: FoodDetailItem, categoryId: string) => {
    const matchingFood = orderDetails.foods.find((food) => isSameCartFood(food, item) && food.item_category_id === categoryId);

    if (matchingFood) {
      const newData = {
        ...item,
        food: matchingFood,
        categoryId,
      };
      setCustomDishData(newData);
      setOpenCustomDish(true);
    }
  };

  const canIncrementQuantity = (itemId: string, targetQuantity: number = 1) => {
    const foodItem = allFoodsFromCategories.find((f) => f.id === itemId || f.food_id === itemId);
    if (!foodItem) return true;

    if (isPreOrder) {
      const quantityStatus = getPreOrderQuantityStatusForCartItem({
        food: foodItem,
        orderDate: seatInfo?.order_date,
        cartFoods: [...(orderDetails.foods ?? []), ...(orderDetails.promotion_foods ?? [])],
        itemFoodId: itemId,
        newQuantity: targetQuantity,
        getMasterFoodId: getMasterId,
      });
      if (quantityStatus.exceedsLimit) {
        setOpenDialog({ value: true, type: 'pre_order_quantity', maxQty: quantityStatus.remainingQuantity });
        return false;
      }
    }

    return true;
  };

  const handleSetCount = (newCount: number, itemId: string, _categoryId: string) => {
    const masterId = getMasterId(itemId);

    if (newCount === 0) {
      const updatedOrderDetails = orderDetails.foods.filter((food: FoodItem) => getMasterId(food.item_food_id) !== masterId);
      updateOrderDetails({ ...orderDetails, foods: updatedOrderDetails });
    } else {
      const existingFoodIndex = orderDetails.foods.findIndex((food: FoodItem) => getMasterId(food.item_food_id) === masterId);
      const existingFood = existingFoodIndex !== -1 ? orderDetails.foods[existingFoodIndex] : undefined;
      const currentCount = existingFood?.item_quantity || 0;

      if (newCount > currentCount) {
        if (!canIncrementQuantity(itemId, newCount)) return;
      }

      if (existingFoodIndex !== -1) {
        const foodDetail = allFoodsFromCategories.find((food) => getMasterId(food.id, food.food_id) === masterId);
        runWithAgeVerification(newCount > currentCount && (existingFood?.is_alcoholic || foodDetail?.is_alcoholic), () => {
          const updatedOrderDetails = orderDetails.foods.map((food: FoodItem, idx: number) => {
            if (idx === existingFoodIndex) {
              return { ...food, item_quantity: newCount, item_subtotal: newCount * food.item_price };
            }
            return food;
          });
          updateOrderDetails({ ...orderDetails, foods: updatedOrderDetails });
        });
      }
    }
  };

  const calculateFoodDrinkCount = (foodsList: any[], excludedId?: string, additionalItem?: any) => {
    let foodCount = 0;
    let drinkCount = 0;

    if (!allFoodsFromCategories || allFoodsFromCategories.length === 0) {
      return { foodCount, drinkCount };
    }

    const filteredFoods = excludedId ? foodsList.filter((item) => getMasterId(item.item_food_id) !== getMasterId(excludedId)) : foodsList;

    filteredFoods.forEach((item) => {
      const foodItem = allFoodsFromCategories.find((f) => f.id === item.item_food_id);
      const foodType = foodItem?.food_type || 'food';

      if (foodType === 'drink') {
        drinkCount += item.item_quantity;
      } else {
        foodCount += item.item_quantity;
      }
    });

    if (additionalItem) {
      const newFoodItem = allFoodsFromCategories.find((f) => f.id === additionalItem.id);
      const newFoodType = newFoodItem?.food_type || 'food';

      if (newFoodType === 'drink') {
        drinkCount += additionalItem.quantity;
      } else {
        foodCount += additionalItem.quantity;
      }
    }

    return { foodCount, drinkCount };
  };

  const checkFoodDrinkLimit = (foodCount: number | null, drinkCount: number | null) => {
    if (enable_food_quantity_control && foodCount !== null && foodCount > max_food_quantity) {
      setOpenDialog({ value: true, type: 'food' });
      return true;
    }

    if (enable_drink_quantity_control && drinkCount !== null && drinkCount > max_drink_quantity) {
      setOpenDialog({ value: true, type: 'drink' });
      return true;
    }

    return false;
  };

  const handleCloseDialog = () => {
    setOpenDialog({ value: false, type: '' });
  };

  const handleFormSubmitDish = (data: any) => {
    if (!canIncrementQuantity(data.id, data.quantity)) return;

    const foodItem = allFoodsFromCategories.find((f) => f.id === data.id);
    if (!foodItem) return;
    if (!isPreOrder && foodItem.enabled === false) {
      return;
    }
    if (isPreOrder) {
      const restaurant = listRestaurantData?.find((item) => item.id === foodItem.restaurantId);
      const preOrderStatus = isPreOrderDisabled(foodItem, restaurant, seatInfo?.order_date, isPreOrder, orderDetails.delivery_request, gameDayData);
      if (preOrderStatus.disabled) {
        return;
      }
    }
    // Add restaurantId to data if available
    if (foodItem?.restaurantId) {
      data.restaurantId = foodItem.restaurantId;
    }

    setShowCart(true);
    addItemsToCart(data);
    setOpenCustomDish(false);

    if (openedFromRecommended) {
      // Came from Recommended Dialog → go back to it after updating cart
      setOpenedFromRecommended(false);
      setShowRecommendedDialog(true);
    } else {
      setOpenedFromRecommended(false);
      // Show recommended foods dialog after adding to cart (normal flow)
      const recommendedFoodItems = getRecommendedFoods(data.id, data.category_id);
      if (recommendedFoodItems.length > 0) {
        setRecommendedFoods(recommendedFoodItems);
        setShowRecommendedDialog(true);
      }
    }
  };

  function addItemsToCart(item: any) {
    const items = [item, ...(Array.isArray(item.recommendedItems) ? item.recommendedItems : [])];
    const foods = items.reduce((currentFoods: FoodItem[], currentItem: any) => {
      const filteredFoods = currentFoods.filter((food) => !isSameCartFood(food, currentItem));
      const newItemData: FoodItem = {
        item_category_id: currentItem.category_id,
        item_food_id: currentItem.id,
        item_price: currentItem.price,
        item_name: currentItem.name,
        item_note: currentItem.note,
        item_quantity: currentItem.quantity,
        item_subtotal: currentItem.total_price,
        sku: currentItem.sku,
        tax_rate: currentItem.tax_rate,
        is_alcoholic: currentItem.is_alcoholic || false,
        item_customize_settings: currentItem.chooseToppings,
        restaurantId: currentItem.restaurantId,
      };

      return [...filteredFoods, newItemData];
    }, orderDetails.foods);

    updateOrderDetails({ ...orderDetails, foods });
  }

  const handleDelivery = () => {
    if (orderDetails.foods.length === 0) {
      return;
    }
    const { availableFoods, availablePromotionFoods } = getCurrentAvailableCartItems();
    const removedCount = orderDetails.foods.length + (orderDetails.promotion_foods ?? []).length - availableFoods.length - availablePromotionFoods.length;

    if (removedCount > 0) {
      updateOrderDetails({
        ...orderDetails,
        foods: availableFoods,
        promotion_foods: availablePromotionFoods,
      });
      return;
    }

    updateOrderDetails(orderDetails);
    const deliveryDetailLink = queryString.stringifyUrl({
      url: `/delivery`,
      query: queryParams,
    });
    navigate(deliveryDetailLink);
  };

  const handleFoodClick = (food: FoodDetailItem, categoryId: string) => {
    if (!food.restaurantId) return;

    const restaurant = listRestaurantData?.find((item) => item.id === food.restaurantId);
    if (!isPreOrder && food.enabled === false) {
      return;
    }
    const preOrderStatus = isPreOrderDisabled(food, restaurant, seatInfo?.order_date, isPreOrder, orderDetails.delivery_request, gameDayData);
    if (preOrderStatus.disabled) {
      return;
    }

    // Check if food already in cart
    const existingFood = orderDetails.foods.find((f) => isSameCartFood(f, food) && f.item_category_id === categoryId);

    if (existingFood) {
      editItemOrder(food, categoryId);
    } else {
      toggleCart(food, categoryId);
    }
  };

  const handleDeleteClick = (foodId: string, categoryId: string) => {
    const food = activeCategory?.foods.find((f) => f.id === foodId);
    setDeleteConfirmDialog({
      open: true,
      foodId,
      categoryId,
      foodName: food?.name || '',
    });
  };

  const handleConfirmDelete = () => {
    const masterId = getMasterId(deleteConfirmDialog.foodId);
    const updatedOrderDetails = orderDetails.foods.filter((food: FoodItem) => getMasterId(food.item_food_id) !== masterId);
    updateOrderDetails({ ...orderDetails, foods: updatedOrderDetails });
    setDeleteConfirmDialog({ open: false, foodId: '', categoryId: '', foodName: '' });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmDialog({ open: false, foodId: '', categoryId: '', foodName: '' });
  };

  useEffect(() => {
    if (!stadiumInfo?.id || !seatInfo?.id) {
      const orderDetailLink = queryString.stringifyUrl({
        url: `/`,
        query: queryParams,
      });
      return navigate(orderDetailLink);
    }
  }, [seatInfo?.id, stadiumInfo?.id]);

  useEffect(() => {
    if (!activeCategoryId && homeMenuCategories.length > 0) {
      setActiveCategoryId(homeMenuCategories[0].id);
    }
  }, [activeCategoryId, homeMenuCategories]);

  useEffect(() => {
    if (homeMenuCategories.length > 0) {
      const allFoods = homeMenuCategories.flatMap((cat) =>
        cat.foods.map((food) => ({
          category: cat.title,
          id: food.id,
          food_id: food.food_id,
          name: food.name,
          enabled_pre_order: food.enabled_pre_order,
          pre_order_quantity: food.pre_order_quantity,
          pre_order_sold: food.pre_order_sold,
        }))
      );
    }
  }, [homeMenuCategories]);

  useEffect(() => {
    if (orderDetails.foods.length > 0) {
      setShowCart(true);
      const updatedActiveItems: { [key: string]: number } = {};

      const masterTotals: { [key: string]: number } = {};
      orderDetails.foods.forEach((food) => {
        const masterId = getMasterId(food.item_food_id);
        masterTotals[masterId] = (masterTotals[masterId] || 0) + food.item_quantity;
      });

      homeMenuCategories.forEach((cat) => {
        cat.foods.forEach((f) => {
          const mId = f.food_id || f.id;
          if (masterTotals[mId] !== undefined) {
            const exactKey = `${f.id}_${cat.id}`;
            updatedActiveItems[exactKey] = masterTotals[mId];
          }
        });
      });

      orderDetails.foods.forEach((food) => {
        const masterId = getMasterId(food.item_food_id);
        const exactKey = `${food.item_food_id}_${food.item_category_id}`;
        if (updatedActiveItems[exactKey] === undefined) {
          updatedActiveItems[exactKey] = masterTotals[masterId] || food.item_quantity;
        }
      });

      setActiveFood(updatedActiveItems);
    } else {
      setActiveFood({});
      setShowCart(false);
    }
  }, [orderDetails, homeMenuCategories]);

  useEffect(() => {
    if (homeMenuCategories.length === 0) return;

    if (isPreOrder) {
      if (!gameDayData) return;

      const restaurantPreOrderRules = [...listRestaurantWithFood, ...listRestaurantData].map((restaurant) => (restaurant as any).preorder_rules).filter(Boolean);
      const preOrderRules = stadiumInfo?.pre_order_rules?.preorder_rules;
      const availableDeliveryRequests = generateTimeSlots(gameDayData, preOrderRules, true, restaurantPreOrderRules).map((option) => option.value);

      if (!orderDetails.delivery_request || !availableDeliveryRequests.includes(orderDetails.delivery_request)) {
        updateOrderDetails({
          ...orderDetails,
          delivery_request: availableDeliveryRequests[0] || '',
        });
        return;
      }
    }

    if (orderDetails.foods.length === 0 && (orderDetails.promotion_foods ?? []).length === 0) return;

    const availableFoods = isPreOrder ? filterAvailablePreOrderCartItems(orderDetails.foods) : filterAvailableSameDayCartItems(orderDetails.foods);
    const availablePromotionFoods = isPreOrder
      ? filterAvailablePreOrderCartItems(orderDetails.promotion_foods ?? [])
      : filterAvailableSameDayCartItems(orderDetails.promotion_foods ?? []);

    if (availableFoods.length === orderDetails.foods.length && availablePromotionFoods.length === (orderDetails.promotion_foods ?? []).length) {
      return;
    }

    updateOrderDetails({
      ...orderDetails,
      foods: availableFoods,
      promotion_foods: availablePromotionFoods,
    });
  }, [
    isPreOrder,
    orderDetails.delivery_request,
    orderDetails.foods,
    orderDetails.promotion_foods,
    gameDayData,
    homeMenuCategories,
    listRestaurantData,
    listRestaurantWithFood,
  ]);

  useEffect(() => {
    if (!seatInfo?.id) return;

    if (orderDetails.foods.length > 0) {
      const firstFood = allFoodsFromCategories.find((f) => f.id === orderDetails.foods[0].item_food_id);
      if (firstFood?.restaurantId) {
        orderDetails.order_table_number = seatInfo.id;
        orderDetails.stadium_id = stadiumInfo?.id || '';
        orderDetails.seat_type = seatInfo.seat_type;
        orderDetails.seat_number = seatInfo.seat_number;
        updateOrderDetails(orderDetails);
      }
    }
  }, [orderDetails.foods.length, seatInfo?.id]);

  const activeCategory = useMemo(() => {
    return homeMenuCategories.find((c) => c.id === activeCategoryId) ?? homeMenuCategories[0];
  }, [activeCategoryId, homeMenuCategories]);

  const masterQuantities = useMemo(() => {
    const counts: { [key: string]: number } = {};
    orderDetails.foods.forEach((f) => {
      const masterId = getMasterId(f.item_food_id);
      counts[masterId] = (counts[masterId] || 0) + f.item_quantity;
    });
    return counts;
  }, [orderDetails.foods, allFoodsFromCategories]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {loading || loadingFoods || loadingFoodTrucks ? (
        <div className="flex items-center justify-center h-screen">
          <CircularProgress color="success" size={65} />
        </div>
      ) : homeMenuCategories.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-screen gap-4 px-8 text-center">
          <img src={sleepIcon} alt="no food" className="w-20 h-20" />
          <p className="text-xl font-semibold text-gray-700">{t('NO_FOOD_AVAILABLE')}</p>
          <p className="text-sm text-gray-400">{t('NO_FOOD_AVAILABLE_DESC')}</p>
        </div>
      ) : (
        <>
          <SearchBar
            input={input}
            isInputFocused={isInputFocused}
            searchResult={searchResult}
            onSearch={handleSearch}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            onFoodClick={handleFoodClick}
          />

          {/* CONTENT */}
          <div className="flex flex-col gap-6 pt-4 pb-16">
            {showNoticeBanner && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: '#ffffff',
                  padding: '12px 16px',
                }}
              >
                <p style={{ flex: 1, fontSize: '13px', color: '#374151', lineHeight: '1.6', margin: 0 }}>{t('ORDER_NOTICE_BANNER')}</p>
                <button
                  onClick={() => {
                    localStorage.setItem('order_notice_dismissed', 'true');
                    setShowNoticeBanner(false);
                  }}
                  style={{
                    flexShrink: 0,
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '6px 14px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('NOTICE_BUTTON')}
                </button>
              </div>
            )}
            {homeMenuCategories.length > 0 && (
              <div className="px-4">
                <div className="mb-6 -mx-4">
                  <ListFoodSuggested activeCategory={activeCategory} onFoodClick={handleFoodClick} />
                </div>

                <div className="-mx-4">
                  <CategoryTabs categories={homeMenuCategories} activeCategoryId={activeCategoryId} onCategoryChange={setActiveCategoryId} />
                </div>

                <div className="mt-6">
                  <FoodList
                    category={activeCategory}
                    activeFood={activeFood}
                    getRestaurantName={getRestaurantName}
                    onFoodClick={handleFoodClick}
                    onSetCount={handleSetCount}
                    onDeleteClick={handleDeleteClick}
                    listRestaurantData={listRestaurantData}
                    orderDate={seatInfo?.order_date}
                    isPreOrder={isPreOrder}
                    deliveryRequest={orderDetails.delivery_request}
                    gameDayData={gameDayData}
                  />
                </div>
              </div>
            )}
          </div>

          {showCart && <FloatingCart animation={cartAnimation} totalCount={FoodTotalCount} totalPrice={TotalOrderPrice} onDelivery={handleDelivery} />}

          {/* Customize Dish Drawer */}
          <Drawer
            anchor={'right'}
            transitionDuration={600}
            open={openCustomDish}
            onClose={() => {
              toggleDrawer(false)();
              if (openedFromRecommended) {
                setOpenedFromRecommended(false);
                setShowRecommendedDialog(true);
              }
            }}
            keepMounted={false}
            ModalProps={{
              disableEnforceFocus: true,
              disableAutoFocus: true,
              disableRestoreFocus: true,
              disableScrollLock: true,
            }}
          >
            <CustomizeDish
              toggleDrawer={toggleDrawer}
              onSubmit={handleFormSubmitDish}
              dishData={customDishData}
              restaurantFoodMap={restaurantFoodMap}
              orderDetails={orderDetails}
              enableAgeVerification
              onDrawerClose={() => {
                if (openedFromRecommended) {
                  setOpenedFromRecommended(false);
                  setShowRecommendedDialog(true);
                }
              }}
              onRecommendedFoodClick={(food, categoryId) => {
                setOpenCustomDish(false);
                setTimeout(() => {
                  setCustomDishData({ ...food, categoryId });
                  setOpenCustomDish(true);
                }, 650);
              }}
            />
          </Drawer>

          {/* Recommended Food Dialog */}
          <RecommendedFoodDialog
            open={showRecommendedDialog}
            onClose={(event, reason) => {
              if (reason !== 'backdropClick') setShowRecommendedDialog(false);
            }}
            onGoToCart={() => {
              setShowRecommendedDialog(false);
              handleDelivery();
            }}
            recommendedFoods={recommendedFoods}
            orderDetails={orderDetails}
            masterQuantities={masterQuantities}
            listRestaurantData={listRestaurantData}
            orderDate={seatInfo?.order_date ?? null}
            isPreOrder={isPreOrder}
            deliveryRequest={orderDetails.delivery_request}
            gameDayData={gameDayData}
            onFoodClick={(food) => {
              const restaurant = listRestaurantData?.find((item) => item.id === food.restaurantId);
              const preOrderStatus = isPreOrderDisabled(food, restaurant, seatInfo?.order_date, isPreOrder, orderDetails.delivery_request, gameDayData);
              if ((!isPreOrder && food.enabled === false) || (isPreOrder && preOrderStatus.disabled)) {
                return;
              }

              const existingFood = findExistingCartFood(food);
              const category = homeMenuCategories.find((cat) => cat.foods.some((f) => f.id === food.id || f.food_id === food.id));
              const categoryId = existingFood?.item_category_id || category?.id || customDishData?.categoryId || food.categories?.[0];
              // Close Recommended Dialog first, then open CustomizeDish
              setShowRecommendedDialog(false);
              setOpenedFromRecommended(true);
              setCustomDishData({ ...food, categoryId });
              setOpenCustomDish(true);
            }}
            onQuickAdd={(food) => {
              const restaurant = listRestaurantData?.find((item) => item.id === food.restaurantId);
              const preOrderStatus = isPreOrderDisabled(food, restaurant, seatInfo?.order_date, isPreOrder, orderDetails.delivery_request, gameDayData);
              if ((!isPreOrder && food.enabled === false) || (isPreOrder && preOrderStatus.disabled)) {
                return false;
              }

              const existingFood = findExistingCartFood(food);
              const category = homeMenuCategories.find((cat) => cat.foods.some((f) => f.id === food.id || f.food_id === food.id));
              const categoryId = existingFood?.item_category_id || category?.id || customDishData?.categoryId || food.categories?.[0];

              const currentCount = existingFood?.item_quantity || 0;

              if (!canIncrementQuantity(food.id, currentCount + 1)) return false;

              if (existingFood) {
                handleSetCount(existingFood.item_quantity + 1, existingFood.item_food_id, categoryId!);
                return true;
              } else {
                const newItemData: FoodItem = {
                  item_category_id: categoryId!,
                  item_food_id: food.id,
                  item_price: food.price,
                  item_name: food.name,
                  item_note: '',
                  item_quantity: 1,
                  item_subtotal: food.price,
                  sku: food.sku,
                  tax_rate: food.tax_rate,
                  is_alcoholic: food.is_alcoholic || false,
                  item_customize_settings: [],
                  restaurantId: food.restaurantId,
                };
                return runWithAgeVerification(food.is_alcoholic, () => updateOrderDetails({ ...orderDetails, foods: [...orderDetails.foods, newItemData] }));
              }
            }}
          />

          <AgeVerificationDialog
            open={openAgeVerification}
            onClose={handleCloseAgeVerification}
            onCancel={handleCancelAgeVerification}
            onConfirm={handleConfirmAgeVerification}
          />
          <AgeVerificationRejectedDialog open={openAgeRejected} onClose={handleCloseAgeRejected} />

          {/* Quantity Limit Dialog */}
          <Dialog
            open={openDialog.value}
            slotProps={{
              backdrop: {
                sx: { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
              },
            }}
            sx={{ '& .MuiPaper-root': { borderRadius: '12px', width: '100%' } }}
          >
            {openDialog.value && (
              <div className="p-6 flex flex-col gap-1 w-full items-center">
                {openDialog.type === 'pre_order_quantity' ? (
                  <>
                    <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('PRE_ORDER_MAX_QUANTITY') || 'Maximum pre-order quantity reached'}</p>
                    <p className="text-gray-500 text-sm text-center mt-1">
                      {t('PRE_ORDER_MAX_QUANTITY_DESC', {
                        quantity: openDialog.maxQty,
                        defaultValue: 'You cannot order more than {{quantity}} items for this product.',
                      })}
                    </p>
                  </>
                ) : (
                  <>
                    <p
                      className="text-black-900 text-lg font-semibold leading-7 text-center"
                      dangerouslySetInnerHTML={{ __html: t('MAXIMUN_QUANTITY', { tag: openDialog.type === 'drink' ? t('DRINK') : t('FOOD') }) }}
                    />
                    <p className="text-black-900 text-lg font-semibold leading-7 text-center">
                      {openDialog.type === 'drink' ? t('DRINK_MAX', { num: max_drink_quantity }) : t('FOOD_MAX', { num: max_food_quantity })}
                    </p>
                    <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('PLEASE_TRY_AGAIN')}</p>
                  </>
                )}
                <button
                  className="px-18 mt-2 py-2.5 rounded-lg border border-solid shadow-button text-base font-semibold leading-6 bg-primary-green border-primary-green text-white"
                  onClick={handleCloseDialog}
                >
                  {t('OK')}
                </button>
              </div>
            )}
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmDialog.open} onClose={handleCancelDelete} sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}>
            <div className="p-6 flex flex-col gap-6 w-full items-center">
              <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('CONFIRM_DELETE_ITEM')}</p>
              {deleteConfirmDialog.foodName && <p className="text-gray-700 text-base font-medium text-center">{deleteConfirmDialog.foodName}</p>}
              <div className="flex gap-3 w-full">
                <button
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-base font-semibold leading-6 bg-white text-gray-700"
                  onClick={handleCancelDelete}
                >
                  {t('CANCEL')}
                </button>
                <button
                  className="flex-1 px-4 py-2.5 rounded-lg border border-solid shadow-button text-base font-semibold leading-6 bg-red-500 border-red-500 text-white"
                  onClick={handleConfirmDelete}
                >
                  {t('DELETE')}
                </button>
              </div>
            </div>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default HomeTabNew;
